import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { PartnerService, Partner } from '@/services/partnerService';
import { getPartnerLogoUrl } from '@/config/api';
import { Ticket } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const PartnersCarousel = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [partnersWithVouchers, setPartnersWithVouchers] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPartners = async () => {
      try {
        setLoading(true);
        const partnersData = await PartnerService.getAllPartners();
        setPartners(partnersData);

        const partnersWithActiveVouchers = new Set<number>();
        for (const partner of partnersData) {
          const v = partner.has_active_vouchers;
          if (v === true || v === 1) {
            partnersWithActiveVouchers.add(partner.id);
          }
        }

        setPartnersWithVouchers(partnersWithActiveVouchers);
      } catch (error) {
        console.error('Erro ao carregar parceiros:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPartners();
  }, []);

  // Randomizar e selecionar 8 parceiros
  const displayedPartners = useMemo(() => {
    if (partners.length === 0) return [];
    
    // Criar uma cópia do array para não modificar o original
    const shuffled = [...partners];
    
    // Algoritmo Fisher-Yates para embaralhar
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    
    // Pegar apenas os primeiros 8 parceiros aleatórios
    return shuffled.slice(0, 8);
  }, [partners]);

  const handlePartnerClick = (partnerId: number) => {
    navigate(`/parceiro/${partnerId}`);
  };

  const handleVoucherClick = (e: React.MouseEvent, partnerName: string) => {
    e.stopPropagation(); // Prevenir que o clique no card também seja acionado
    navigate(`/vouchers?partner=${encodeURIComponent(partnerName)}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (partners.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Nenhum parceiro encontrado.</p>
      </div>
    );
  }

  // Dividir os 8 parceiros em duas linhas (4 em cima e 4 em baixo)
  const topPartners = displayedPartners.slice(0, 4);
  const bottomPartners = displayedPartners.slice(4, 8);

  return (
    <div className="space-y-6">
      {/* Primeira linha - 4 parceiros */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {topPartners.map((partner) => (
          <Card
            key={partner.id}
            className="group cursor-pointer overflow-hidden border-0 shadow-medium hover:shadow-large transition-all duration-200 hover:-translate-y-1"
            onClick={() => handlePartnerClick(partner.id)}
          >
            <CardContent className="p-6 text-center relative">
              {partnersWithVouchers.has(partner.id) && (
                <Badge
                  className="absolute top-2 right-2 cursor-pointer hover:scale-105 transition-transform duration-200 z-10"
                  onClick={(e) => handleVoucherClick(e, partner.name)}
                  title="Ver vouchers"
                >
                  <Ticket className="h-4 w-4" />
                </Badge>
              )}
              <div className="mb-4">
                <img
                  src={partner.logo ? getPartnerLogoUrl(partner.logo) : '/live-stay-logo.png'}
                  alt={partner.name}
                  className="w-20 h-20 object-contain mx-auto rounded-lg shadow-soft"
                  onError={(e) => {
                    console.error('Erro ao carregar logo do parceiro:', partner.logo);
                    e.currentTarget.src = '/live-stay-logo.png';
                  }}
                />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                {partner.name}
              </h3>
              {partner.phone && (
                <p className="text-sm text-muted-foreground">
                  {partner.phone}
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Segunda linha - 4 parceiros */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {bottomPartners.map((partner) => (
          <Card
            key={partner.id}
            className="group cursor-pointer overflow-hidden border-0 shadow-medium hover:shadow-large transition-all duration-200 hover:-translate-y-1"
            onClick={() => handlePartnerClick(partner.id)}
          >
            <CardContent className="p-6 text-center relative">
              {partnersWithVouchers.has(partner.id) && (
                <Badge
                  className="absolute top-2 right-2 cursor-pointer hover:scale-105 transition-transform duration-200 z-10"
                  onClick={(e) => handleVoucherClick(e, partner.name)}
                  title="Ver vouchers"
                >
                  <Ticket className="h-4 w-4" />
                </Badge>
              )}
              <div className="mb-4">
                <img
                  src={partner.logo ? getPartnerLogoUrl(partner.logo) : '/live-stay-logo.png'}
                  alt={partner.name}
                  className="w-20 h-20 object-contain mx-auto rounded-lg shadow-soft"
                  onError={(e) => {
                    console.error('Erro ao carregar logo do parceiro:', partner.logo);
                    e.currentTarget.src = '/live-stay-logo.png';
                  }}
                />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                {partner.name}
              </h3>
              {partner.phone && (
                <p className="text-sm text-muted-foreground">
                  {partner.phone}
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default PartnersCarousel;
